import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { FautrasLogo } from "@/components/FautrasLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { LogIn, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next");
  const safeNext = next && next.startsWith("/") && !next.startsWith("//") ? next : null;
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success("Connexion réussie.");
      if (u.must_change_password && u.role !== "admin") navigate("/compte");
      else if (safeNext) navigate(safeNext);
      else navigate(u.role === "admin" ? "/admin" : "/");
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Email ou mot de passe incorrect.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#434343] px-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-8">
          <FautrasLogo dark />
        </div>
        <form onSubmit={submit} data-testid="login-form"
          className="bg-white rounded-2xl p-7 sm:p-8 shadow-xl">
          <h1 className="font-heading text-2xl font-extrabold text-[#2b2b2b]">Espace vendeur</h1>
          <p className="text-sm text-muted-foreground mt-1 mb-6">Connectez-vous pour accéder au simulateur sans étape de coordonnées.</p>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-semibold">Email</Label>
              <Input id="email" type="email" required value={email} data-testid="login-email"
                onChange={(e) => setEmail(e.target.value)} className="mt-2 h-12" placeholder="vous@exemple.fr" />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-semibold">Mot de passe</Label>
              <div className="relative mt-2">
                <Input id="password" type={showPwd ? "text" : "password"} required value={password} data-testid="login-password"
                  onChange={(e) => setPassword(e.target.value)} className="h-12 pr-11" placeholder="••••••••" />
                <button type="button" onClick={() => setShowPwd((v) => !v)} data-testid="login-toggle-password"
                  aria-label={showPwd ? "Masquer le mot de passe" : "Afficher le mot de passe"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-[#9E7B41]">
                  {showPwd ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>
          </div>
          <div className="mt-2 text-right">
            <button type="button" onClick={() => navigate("/mot-de-passe-oublie")} data-testid="login-forgot"
              className="text-sm font-medium text-[#9E7B41] hover:underline underline-offset-2">Mot de passe oublié ?</button>
          </div>
          <Button type="submit" disabled={busy} data-testid="login-submit"
            className="mt-5 w-full h-12 bg-[#9E7B41] hover:bg-[#876634] rounded-xl font-semibold">
            <LogIn className="h-4 w-4 mr-2" /> {busy ? "Connexion…" : "Se connecter"}
          </Button>
          <button type="button" onClick={() => navigate("/")}
            className="mt-4 w-full text-sm text-muted-foreground hover:text-[#9E7B41]" data-testid="login-back">
            ← Retour au simulateur
          </button>
        </form>
      </div>
    </div>
  );
}
